import React, { type FC, useMemo, useState, useCallback } from "react";
import { openInvoice } from "@telegram-apps/sdk";
import { type LookInfo, type LookIdEnum } from "../../handlers/types";
import { useI18n } from "../../utils/i18n/I18nProvider";
import {
  getWeeklySaleCountdown,
  getWeeklySaleItemTitle,
  type WeeklySaleState,
} from "../../utils/weeklySale";
import { getWeeklyLookUpgradeDescription } from "../../utils/lookUtils";
import ApiService from "../../handlers/apiService";
import Game from "../../scripts/game/Game";
import StarIcon from "../Shop/StarIcon";
import "./WeeklyShop.css";

interface WeeklyShopProps {
  weeklySale: WeeklySaleState;
  weeklySecondsRemaining: number;
  className?: string;
  onPurchaseSuccess?: () => void;
  onPurchaseError?: (error: string) => void;
}

function getWeeklyCardRarityClass(look: LookInfo): string {
  if (!look.rarity) return "";
  return `weekly-shop-card--${look.rarity.toLowerCase()}`;
}

export const WeeklyShop: FC<WeeklyShopProps> = ({
  weeklySale,
  weeklySecondsRemaining,
  className = "",
  onPurchaseSuccess,
  onPurchaseError,
}) => {
  const { t } = useI18n();
  const [isPurchasing, setIsPurchasing] = useState<string | null>(null);

  const weeklySaleCountdown = useMemo(
    () => getWeeklySaleCountdown(weeklySecondsRemaining),
    [weeklySecondsRemaining]
  );

  // Filter out owned looks from the weekly sale items
  const availableItems = useMemo(
    () => weeklySale.items.filter((look) => !look.isOwned),
    [weeklySale.items]
  );

  const purchaseLook = useCallback(
    async (lookId: LookIdEnum) => {
      setIsPurchasing(lookId);

      try {
        const response = await ApiService.getInstance().api.looksBuyCreate(
          lookId
        );
        const { invoiceUrl } = response;

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");
          if (status !== "paid") {
            onPurchaseError?.(t("toolkit.shop.errorPayment"));
          } else {
            // Wait for 1 second before updating
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Auto-equip look after successful purchase
            if (Game.Core?.selectLook) {
              try {
                await Game.Core.selectLook(lookId);
                console.log(`Auto-equipped look: ${lookId}`);
              } catch (equipError) {
                console.error("Failed to auto-equip look:", equipError);
                // Don't show error to user as the purchase was successful
              }
            }

            onPurchaseSuccess?.();
          }
        }
      } catch (err) {
        onPurchaseError?.(t("toolkit.shop.errorPurchase"));
      } finally {
        setIsPurchasing(null);
      }
    },
    [onPurchaseSuccess, onPurchaseError, t]
  );

  if (availableItems.length === 0) {
    return null;
  }

  return (
    <div className={`weekly-shop ${className}`}>
      <div className="weekly-shop__header">
        <h2 className="weekly-shop__title">
          {t("toolkit.shop.section.weeklySale", {
            defaultValue: "Weekly Sale",
          })}
        </h2>
        <div className="weekly-shop__timer">{weeklySaleCountdown}</div>
      </div>
      <div className="weekly-shop__items">
        {availableItems.map((look) => (
          <div
            key={look.id}
            className={`weekly-shop-card ${getWeeklyCardRarityClass(look)} ${
              isPurchasing === look.id ? "weekly-shop-card--loading" : ""
            }`}
            onClick={() =>
              isPurchasing === null ? purchaseLook(look.id as LookIdEnum) : null
            }
            style={{
              cursor: isPurchasing === null ? "pointer" : "default",
            }}
          >
            <div className="weekly-shop-card__content">
              <div className="weekly-shop-card__title">
                {getWeeklySaleItemTitle(look, t)}
              </div>
              <div className="weekly-shop-card__sprite">
                <div className="weekly-shop-card__sprite-glow" />
                <div className="look-selector__sprite look-selector__sprite--weekly">
                  <div
                    className="look-selector__sprite-frame"
                    style={{
                      background: `url(/assets/img/characters/${look.spriteKey}.webp) -56px -2px`,
                    }}
                  />
                </div>
              </div>

              {look.rarity && (
                <div className="weekly-shop-card__rarity">
                  {t(`rarity.${look.rarity.toLowerCase()}`, {
                    defaultValue: look.rarity,
                  })}
                </div>
              )}
              <div className="weekly-shop-card__description">
                {getWeeklyLookUpgradeDescription(look, t)}
              </div>
              <div className="weekly-shop-card__price">
                {isPurchasing === look.id ? (
                  <span className="weekly-shop-card__loader"></span>
                ) : look.discountPrice ? (
                  <>
                    <span className="weekly-shop-card__price-original">
                      {look.price}
                    </span>
                    &nbsp;
                    <span>{look.discountPrice}</span>
                  </>
                ) : (
                  <span>{look.price}</span>
                )}
                {isPurchasing !== look.id && <StarIcon />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
